import { useMemo } from 'react';
import {
  RetainerFeeType,
  RetainerWorkType,
  type RetainerFieldsFragment,
} from '~/gql/graphql';
import {
  HOURLY_FEE_OPTIONS,
  type FeeScheduleData,
} from '~/modules/connect/feeSchedule';
import { formatCurrency } from '~/shared/utils/formatCurrency';
import {
  RATE_KEY_OTHER,
  STANDARD_RATE_KEYS,
  WORK_TYPE_OPTIONS,
} from '~/modules/matters/modals/requestRetainerSchema';
import { CENTS_PER_DOLLAR } from '~/shared/constants';

const RATE_MATCH_TOLERANCE = 0.01;

export interface RetainerHourlyRateDisplayContext {
  rateKey: string;
  customServiceLabel: string | undefined;
}

/**
 * Retainers do not persist form `rateKey` / `customServiceLabel`. Match stored hourly fee (cents)
 * to the expert fee schedule when unambiguous; otherwise use work-type / `additionalDetails` for
 * “Other (custom rate)” style labeling.
 */
export function getRetainerHourlyRateDisplayContext(
  retainer: RetainerFieldsFragment,
  feeSchedule: FeeScheduleData,
): RetainerHourlyRateDisplayContext {
  if (retainer.feeType !== RetainerFeeType.Hourly) {
    return { rateKey: 'consultationRate', customServiceLabel: undefined };
  }

  const hourlyDollars =
    retainer.hourlyFee !== null && retainer.hourlyFee !== undefined
      ? retainer.hourlyFee / CENTS_PER_DOLLAR
      : 0;

  const matchingKeys = STANDARD_RATE_KEYS.filter(
    (key) => Math.abs(feeSchedule[key] - hourlyDollars) <= RATE_MATCH_TOLERANCE,
  );
  if (matchingKeys.length === 1) {
    return { rateKey: matchingKeys[0], customServiceLabel: undefined };
  }

  const workTypeLabel = WORK_TYPE_OPTIONS.find(
    (option) => option.value === retainer.workType,
  )?.label;

  if (retainer.workType === RetainerWorkType.SomethingElse) {
    const firstLine = retainer.additionalDetails?.split(/\r?\n/)[0]?.trim();
    return {
      rateKey: RATE_KEY_OTHER,
      customServiceLabel: firstLine || workTypeLabel,
    };
  }

  return {
    rateKey: RATE_KEY_OTHER,
    customServiceLabel: workTypeLabel,
  };
}

export function getRateDisplayLabel(
  rateKey: string,
  customServiceLabel: string | undefined,
  hourlyRate: number,
): string {
  const formattedRate = `${formatCurrency(hourlyRate)}/hr`;
  if (rateKey === RATE_KEY_OTHER) {
    const prefix = customServiceLabel || 'Other';
    return `${prefix} — ${formattedRate}`;
  }
  const optionLabel =
    HOURLY_FEE_OPTIONS.find((o) => o.key === rateKey)?.label ?? '';
  return `${optionLabel} — ${formattedRate}`;
}

export function useRateDisplayLabel(
  rateKey: string,
  customServiceLabel: string | undefined,
  hourlyRate: number,
): string {
  return useMemo(
    () => getRateDisplayLabel(rateKey, customServiceLabel, hourlyRate),
    [rateKey, customServiceLabel, hourlyRate],
  );
}

export function computeBillingSummary(
  isProBono: boolean,
  isFlatRate: boolean,
  hours: number,
  hourlyRate: number,
  flatAmount: number,
  platformFeeRate: number,
): { subtotal: number; platformFee: number; total: number } {
  const subtotal = (() => {
    if (isProBono) {
      return 0;
    }
    if (isFlatRate) {
      return flatAmount;
    }
    return hours * hourlyRate;
  })();
  const platformFee = subtotal * platformFeeRate;
  const total = subtotal + platformFee;
  return { subtotal, platformFee, total };
}
